import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Navbar,
  Nav,
  Button,
  Badge,
  Form,
  Dropdown,
  InputGroup,
} from 'react-bootstrap';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaSignOutAlt,
  FaUser,
  FaCog as FaSettings,
  FaSearch,
  FaWhatsapp,
  FaPhone,
  FaChevronDown,
  FaTh,
  FaThList,
} from 'react-icons/fa';
import { BsTrash } from 'react-icons/bs';
import ManageTabsModal from './ManageTabsModal';
import jwt_decode from 'jwt-decode';
import authApi from '../../api/authApi';
import DataApi from '../../api/DataApi';
import { clearPermissionCache } from '../../utils/permissionCache';
import {
  getEntitlementsSection,
  canAccessSetup,
  hasPackage,
  ENTITLEMENTS_UPDATED_EVENT,
  ENTITLEMENTS_KEY,
} from '../../utils/entitlements';
import { formatDate, formatDateTime, formatTime } from '../../utils/formatUtils';
import Menu from './Menu';
import NotificationList from '../notification/NotificationList';
import SystemNotificationDetailModal from '../notification/SystemNotificationDetailModal';
import Notification from '../common/Notification';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import AppIconDisplay from './AppIconDisplay';
import GlobalSearchPalette from '../search/GlobalSearchPalette';
import '../search/GlobalSearch.css';
import TelecmiApi from '../../api/TelecmiApi';
import {
  emitTelecmiOutboundFailed,
  emitTelecmiOutboundWaiting,
} from '../../utils/telecmiDialerBus';
import { TELECMI_SP_TOKEN_KEY } from '../../utils/telecmiSoftphoneSession';
import { buildTelecmiInitiatePayload } from '../../utils/telecmiInitiatePayload';
import { parseApiToastHints } from '../../utils/apiErrorMessage';
import './css/AppHeader.css';

const SELECTED_APP_STORAGE_KEY = 'vgrow_selected_app_id';
const RECENT_APPS_STORAGE_KEY = 'vgrow_app_launcher_recent';
const RECENT_APPS_MAX = 3;

const TELECMI_SP_AGENT_KEY = 'ibs_telecmi_softphone_agent';

function readTelecmiSoftphoneSession() {
  try {
    const token = sessionStorage.getItem(TELECMI_SP_TOKEN_KEY);
    const agentRaw = sessionStorage.getItem(TELECMI_SP_AGENT_KEY);
    if (!token || !agentRaw) return null;
    const agent = JSON.parse(agentRaw);
    return { token, agent };
  } catch {
    return null;
  }
}

function writeTelecmiSoftphoneSession(token, agent) {
  sessionStorage.setItem(TELECMI_SP_TOKEN_KEY, token);
  sessionStorage.setItem(TELECMI_SP_AGENT_KEY, JSON.stringify(agent));
}

function clearTelecmiSoftphoneSession() {
  sessionStorage.removeItem(TELECMI_SP_TOKEN_KEY);
  sessionStorage.removeItem(TELECMI_SP_AGENT_KEY);
}

function getRecentAppIds() {
  try {
    const raw = localStorage.getItem(RECENT_APPS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_APPS_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecentAppId(appId) {
  if (!appId) return;
  const recent = getRecentAppIds().filter((id) => id !== appId);
  recent.unshift(appId);
  localStorage.setItem(RECENT_APPS_STORAGE_KEY, JSON.stringify(recent.slice(0, RECENT_APPS_MAX)));
}

const SYSTEM_NOTIFICATION_DURATION_MS = 10000;

const AppHeader = ({ socket, crmSocket, onWhatsAppSettingChange }) => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({});
  const [whatsappSetting, setWhatsappSetting] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState(
    sessionStorage.getItem('selectedWhatsAppSetting') || ''
  );
  const [apps, setApps] = useState([]);
  const [appsFetchCompleted, setAppsFetchCompleted] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(
    () => localStorage.getItem(SELECTED_APP_STORAGE_KEY) || ''
  );
  const [appLauncherSearch, setAppLauncherSearch] = useState('');
  const [showAppLauncher, setShowAppLauncher] = useState(false);
  const appLauncherToggleRef = useRef(null);
  const appLauncherPanelRef = useRef(null);
  const seenNotificationIdsRef = useRef(new Set());
  const [launcherPosition, setLauncherPosition] = useState(null);
  const [showManageTabsModal, setShowManageTabsModal] = useState(false);

  const updateLauncherPosition = (anchorEl) => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const minLeft = 24;
    const panelWidth = 420;
    let left = rect.left;
    if (left < minLeft) left = minLeft;
    if (left + panelWidth > window.innerWidth - 16) left = Math.max(minLeft, window.innerWidth - panelWidth - 16);
    setLauncherPosition({ top: rect.bottom + 4, left });
  };

  // Update position on scroll/resize when launcher is open
  useLayoutEffect(() => {
    if (!showAppLauncher || !appLauncherToggleRef.current) {
      if (!showAppLauncher) setLauncherPosition(null);
      return;
    }
    updateLauncherPosition(appLauncherToggleRef.current);
    const updatePosition = () => {
      if (appLauncherToggleRef.current) updateLauncherPosition(appLauncherToggleRef.current);
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showAppLauncher]);

  // Close App Launcher on click outside
  useEffect(() => {
    if (!showAppLauncher) return;
    const handleClickOutside = (e) => {
      const panel = appLauncherPanelRef.current;
      const toggle = appLauncherToggleRef.current;
      if (panel?.contains(e.target) || toggle?.contains(e.target)) return;
      setShowAppLauncher(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAppLauncher]);

  const [unreadMessages, setUnreadMessages] = useState([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [whatsappOffset, setWhatsappOffset] = useState(0);
  const [hasMoreWhatsapp, setHasMoreWhatsapp] = useState(false);
  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const globalSearchInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || String(e.key).toLowerCase() !== 'k') {
        return;
      }
      const tag = e.target?.tagName;
      const inPalette = e.target?.closest?.('.gs-palette');
      const inHeaderSearch = e.target?.closest?.('.gs-header-search');
      if (
        !inPalette &&
        !inHeaderSearch &&
        (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      setShowGlobalSearch((open) => !open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!showGlobalSearch) return;
    const t = setTimeout(() => globalSearchInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [showGlobalSearch]);
  const setupConfig = getEntitlementsSection('setup', {});
  const setupMenuItems = [
    'objectmanager',
    'rolemanager',
    'tabs',
    'actions',
    'custom-scripts',
    'workflows',
    'homewidget',
    'webforms',
    'scoringrule',
    'usergroups',
    'users',
    'mailconfig',
    'emailtemplate',
    'emaillayout',
    'assignmentrule',
    'searchviews',
    'globalsearch',
    'session_setting',
    'scheduled-jobs',
    'appmanager',
    'currency',
    'system_settings',
  ];
  const setupPaths = [
    { id: 'objectmanager', path: '/setup/objectmanager' },
    { id: 'rolemanager', path: '/setup/rolemanager' },
    { id: 'tabs', path: '/setup/tabs' },
    { id: 'appmanager', path: '/setup/appmanager' },
    { id: 'actions', path: '/setup/actions' },
    { id: 'custom-scripts', path: '/setup/custom-scripts' },
    { id: 'workflows', path: '/setup/workflows' },
    { id: 'homewidget', path: '/setup/homewidget' },
    { id: 'webforms', path: '/setup/webforms' },
    { id: 'scoringrule', path: '/setup/scoringrule' },
    { id: 'usergroups', path: '/setup/usergroups' },
    { id: 'users', path: '/setup/users' },
    { id: 'mailconfig', path: '/setup/mailconfig' },
    { id: 'emailtemplate', path: '/setup/emailtemplate' },
    { id: 'emaillayout', path: '/setup/emaillayout' },
    { id: 'assignmentrule', path: '/setup/assignmentrule' },
    { id: 'searchviews', path: '/setup/searchviews' },
    { id: 'globalsearch', path: '/setup/globalsearch' },
    { id: 'session_setting', path: '/setup/session_setting' },
    { id: 'scheduled-jobs', path: '/setup/scheduled-jobs' },
    { id: 'currency', path: '/setup/currency/master' },
    { id: 'system_settings', path: '/setup/system_settings' },
  ];
  const allowedSetupItems = new Set(
    (setupConfig?.allowedItems || []).map(item => String(item).toLowerCase())
  );
  const isAllowlist = setupConfig?.mode === 'allowlist';
  const isDenylist = setupConfig?.mode === 'denylist';
  const hasSetupMenuAccess = isAllowlist
    ? allowedSetupItems.size > 0
    : isDenylist
      ? setupMenuItems.some(item => !allowedSetupItems.has(item))
      : true;
  /** Setup icon / profile entry: admin license (see canAccessSetup) + entitlements allowlist/denylist for entry path. */
  const showSetupChrome = canAccessSetup() && hasSetupMenuAccess;
  const getSetupEntryPath = () => {
    if (isAllowlist) {
      const match = setupPaths.find(item => allowedSetupItems.has(item.id));
      return match?.path || '/access-denied';
    }
    if (isDenylist) {
      const match = setupPaths.find(item => !allowedSetupItems.has(item.id));
      return match?.path || '/access-denied';
    }
    return setupPaths[0]?.path || '/access-denied';
  };

  const openSetupInNewTab = () => {
    const path = getSetupEntryPath();
    const rel = path.startsWith('/') ? path : `/${path}`;
    window.open(`${window.location.origin}${rel}`, '_blank', 'noopener,noreferrer');
  };

  const [, setEntitlementsRevision] = useState(0);
  useEffect(() => {
    const bump = () => setEntitlementsRevision(n => n + 1);
    window.addEventListener(ENTITLEMENTS_UPDATED_EVENT, bump);
    const onStorage = e => {
      if (e.key === ENTITLEMENTS_KEY || e.key === null) bump();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ENTITLEMENTS_UPDATED_EVENT, bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // System Notifications State
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [systemUnreadCount, setSystemUnreadCount] = useState(0);
  const [activeNotificationTab, setActiveNotificationTab] = useState('system'); // 'system' or 'whatsapp'
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null); // Track which task is being completed
  const [incomingNotificationToast, setIncomingNotificationToast] = useState({
    show: false,
    item: null,
  });
  const [systemNotificationModalOpen, setSystemNotificationModalOpen] = useState(false);
  const [selectedSystemNotification, setSelectedSystemNotification] = useState(null);
  const [loadingNotificationDetail, setLoadingNotificationDetail] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const [showDeleteAllNotificationsConfirm, setShowDeleteAllNotificationsConfirm] =
    useState(false);
  const [deletingAllNotifications, setDeletingAllNotifications] = useState(false);
  /** Async workflow / OBJECT_VALIDATION_RULE: same toast hints as save API errors */
  const [objectValidationToast, setObjectValidationToast] = useState({
    show: false,
    message: null,
    duration: 8000,
    type: 'danger',
  });

  // WhatsApp Chat State
  const [showChat, setShowChat] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedWhatsAppSetting, setSelectedWhatsAppSetting] = useState('');
  const [showDialer, setShowDialer] = useState(false);
  const [dialerNumber, setDialerNumber] = useState('');
  const [dialerLoading, setDialerLoading] = useState(false);
  const [dialerMessage, setDialerMessage] = useState({ type: '', text: '' });
  const telecmiSpPasswordRef = useRef(null);
  const [telecmiSpSession, setTelecmiSpSession] = useState(null);
  const [telecmiLoginId, setTelecmiLoginId] = useState('');
  const [telecmiLoginPw, setTelecmiLoginPw] = useState('');
  const [telecmiSpBusy, setTelecmiSpBusy] = useState(false);
  const [telecmiSpMsg, setTelecmiSpMsg] = useState({ type: '', text: '' });
  const [telecmiLogoutPw, setTelecmiLogoutPw] = useState('');
  const [telecmiPwInMemory, setTelecmiPwInMemory] = useState(false);
  const dialerKeys = useMemo(
    () => ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'],
    []
  );
  const hasCtiSolutionEnabled = useMemo(
    () =>
      userInfo?.enable_telecmi === true ||
      userInfo?.enable_callyzer === true ||
      userInfo?.enable_vgrow_dialer === true,
    [
      userInfo?.enable_telecmi,
      userInfo?.enable_callyzer,
      userInfo?.enable_vgrow_dialer,
    ]
  );
  const watconnectPackageEnabled = hasPackage('watconnect');

  /** User dropdown: name, username, role (JWT sets fullname on login). */
  const profileMenuLabels = useMemo(() => {
    const full = String(userInfo.fullname || '').trim();
    const fromParts = [userInfo.firstname, userInfo.lastname]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayName =
      full || fromParts || userInfo.username || 'User';
    const forInitials = full || fromParts || userInfo.username || '';
    let initials = 'U';
    if (forInitials) {
      const parts = String(forInitials).split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        initials = (
          (parts[0].charAt(0) || '') +
          (parts[parts.length - 1].charAt(0) || '')
        )
          .toUpperCase()
          .slice(0, 2);
      } else {
        initials = String(forInitials).slice(0, 2).toUpperCase();
      }
    }
    return {
      displayName,
      initials,
      username: userInfo.username || '',
      role: userInfo.role_name || userInfo.userrole || '',
    };
  }, [
    userInfo.fullname,
    userInfo.firstname,
    userInfo.lastname,
    userInfo.username,
    userInfo.role_name,
    userInfo.userrole,
  ]);

  const headerLogoSrc = useMemo(() => {
    const logourl = userInfo?.logourl;
    if (!logourl || typeof logourl !== 'string') return null;
    const roleUpper = String(
      userInfo.userrole || userInfo.role_name || ''
    ).toUpperCase();
    if (roleUpper === 'SUPER_ADMIN') return logourl;
    if (logourl.includes('public')) {
      const relativePath = logourl.split('public')[1].replace(/\\/g, '/');
      return relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    }
    return logourl;
  }, [userInfo?.logourl, userInfo?.userrole, userInfo?.role_name]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const decoded = jwt_decode(token);
      setUserInfo(decoded);
    } catch (error) {
      console.error('Error decoding token:', error);
      navigate('/login');
    }

    if (watconnectPackageEnabled) {
      fetchWhatsAppSettings();
    } else {
      setWhatsappSetting([]);
      setUnreadMessages([]);
      setTotalUnreadCount(0);
      setSelectedWhatsAppSetting('');
      setSelectedSetting('');
    }
  }, [navigate, watconnectPackageEnabled]);

  useEffect(() => {
    if (!showDialer || !userInfo?.enable_telecmi) return;
    setTelecmiSpSession(readTelecmiSoftphoneSession());
    setTelecmiLoginId(String(userInfo.telecmi_user_id || '').trim());
    setTelecmiSpMsg({ type: '', text: '' });
  }, [showDialer, userInfo?.enable_telecmi, userInfo?.telecmi_user_id]);

  useEffect(() => {
    if (!watconnectPackageEnabled && activeNotificationTab === 'whatsapp') {
      setActiveNotificationTab('system');
    }
  }, [watconnectPackageEnabled, activeNotificationTab]);

  const eligibleAppsForLauncher = useMemo(
    () =>
      apps.filter((app) => app?.name != null && String(app.name).trim() !== ''),
    [apps]
  );
  const setupPaletteItems = useMemo(() => {
    const labels = {
      objectmanager: 'Object Manager',
      rolemanager: 'Role Manager',
      tabs: 'Tab Manager',
      appmanager: 'App Manager',
      actions: 'Action Manager',
      'custom-scripts': 'Custom Script Studio',
      workflows: 'Workflow Manager',
      homewidget: 'Home Layouts',
      webforms: 'Web Forms',
      scoringrule: 'Scoring Rules',
      usergroups: 'User Groups',
      users: 'Users',
      mailconfig: 'Mail Config',
      emailtemplate: 'Email Template',
      emaillayout: 'Email Layout',
      assignmentrule: 'Assignment Rules',
      searchviews: 'Search Views',
      globalsearch: 'Global Search Settings',
      session_setting: 'Session Settings',
      'scheduled-jobs': 'Scheduled Jobs',
      currency: 'Currency',
      system_settings: 'System Settings',
    };
    return setupPaths
      .filter((item) => {
        if (!showSetupChrome) return false;
        if (isAllowlist) return allowedSetupItems.has(item.id);
        if (isDenylist) return !allowedSetupItems.has(item.id);
        return true;
      })
      .map((item) => ({
        id: item.id,
        path: item.path,
        label: labels[item.id] || item.id,
      }));
  }, [showSetupChrome, isAllowlist, isDenylist, allowedSetupItems, setupPaths]);
  const showAppSwitcher = appsFetchCompleted && eligibleAppsForLauncher.length > 0;

  // Fetch apps for app selector (tabs from app vs default)
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const appsApi = new DataApi('apps');
        const res = await appsApi.fetchAll();
        if (res?.data?.success && Array.isArray(res.data.data)) {
          setApps(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching apps for header:', err);
      } finally {
        setAppsFetchCompleted(true);
      }
    };
    fetchApps();
  }, []);

  // No managed apps: drop app context so the menu uses default tabs only
  useEffect(() => {
    if (!appsFetchCompleted || eligibleAppsForLauncher.length > 0) return;
    if (selectedAppId) {
      setSelectedAppId('');
      localStorage.removeItem(SELECTED_APP_STORAGE_KEY);
    }
    setShowAppLauncher(false);
  }, [appsFetchCompleted, eligibleAppsForLauncher.length, selectedAppId]);

  const handleAppSelect = (appId) => {
    const id = appId || '';
    setSelectedAppId(id);
    if (id) {
      localStorage.setItem(SELECTED_APP_STORAGE_KEY, id);
      pushRecentAppId(id);
    } else {
      localStorage.removeItem(SELECTED_APP_STORAGE_KEY);
    }
  };

  // Socket listener for real-time message updates
  useEffect(() => {
    if (!socket || !watconnectPackageEnabled) return;

    // Listen for new incoming messages
    const handleNewMessage = data => {
      console.log('New message received via socket:', data);

      // Refresh unread messages count
      fetchUnreadMessages();

      // If chat is open for this user, you might want to refresh the chat
      if (showChat && selectedUser) {
        const socketNumber = (
          data?.recipient_id ||
          data?.to ||
          data?.from ||
          ''
        ).replace(/\D/g, '');
        const currentNumber = selectedUser.whatsapp_number.replace(/\D/g, '');

        if (socketNumber === currentNumber) {
          // Trigger chat refresh if it's the same conversation
          console.log('Refreshing chat for current conversation');
        }
      }
    };

    socket.on('receivedwhatsappmessage', handleNewMessage);
    socket.on('whatsapp_statuses', handleNewMessage);

    // Cleanup
    return () => {
      socket.off('receivedwhatsappmessage', handleNewMessage);
      socket.off('whatsapp_statuses', handleNewMessage);
    };
  }, [socket, showChat, selectedUser, watconnectPackageEnabled]);

  const markNotificationsSeen = useCallback((list) => {
    if (!Array.isArray(list)) return;
    for (const n of list) {
      const id = n?.id ?? n?.notification_id;
      if (id != null) {
        seenNotificationIdsRef.current.add(String(id).toLowerCase());
      }
    }
  }, []);

  const applySystemNotification = useCallback(
    (payload) => {
      const item = payload?.notification;
      if (!item) return;
      const myId = userInfo?.id ? String(userInfo.id).toLowerCase() : '';
      const targetRecipient = payload?.targetRecipientId
        ? String(payload.targetRecipientId).toLowerCase()
        : null;
      if (targetRecipient && myId && targetRecipient !== myId) {
        return;
      }
      const nid = item.id ?? item.notification_id;
      if (nid == null) return;
      const nidKey = String(nid).toLowerCase();
      seenNotificationIdsRef.current.add(nidKey);

      setSystemNotifications((prev) => {
        if (
          prev.some((n) => {
            const existing = n.id ?? n.notification_id;
            return (
              existing != null &&
              String(existing).toLowerCase() === nidKey
            );
          })
        ) {
          return prev;
        }
        return [item, ...prev].slice(0, 50);
      });

      const unread =
        item.is_read === false || item.is_read === 'false' || item.is_read == null;
      if (unread) {
        setSystemUnreadCount((c) => c + 1);
      }

      if (item.notification_type === 'workflow_validation_blocked') {
        return;
      }

      setIncomingNotificationToast({ show: true, item });
    },
    [userInfo?.id]
  );

  // Socket forwards to ibs-system-notification in App.js; Run now / poll use the same event
  useEffect(() => {
    const onDirect = (event) => {
      if (event?.detail) {
        applySystemNotification(event.detail);
      }
    };
    window.addEventListener('ibs-system-notification', onDirect);
    return () => window.removeEventListener('ibs-system-notification', onDirect);
  }, [applySystemNotification]);

  useEffect(() => {
    if (!crmSocket) return undefined;

    const onObjectValidationToast = (payload) => {
      const messageText =
        payload?.message != null && String(payload.message).trim()
          ? String(payload.message)
          : 'Validation failed';
      const title =
        payload?.title != null && String(payload.title).trim()
          ? String(payload.title)
          : 'Workflow failed';
      const hints = parseApiToastHints(payload?.toast);
      const sticky =
        payload?.workflowFailure === true || hints?.sticky === true;
      // Workflow / validation flyouts: dismiss only manually (no auto-hide).
      const duration = sticky ? 0 : hints?.delay != null ? hints.delay : 8000;
      const fs = hints?.messageFontSize;
      const inner = (
        <div style={fs ? { fontSize: fs } : undefined}>
          <div className='fw-semibold'>{title}</div>
          <div className='small mt-1'>{messageText}</div>
        </div>
      );
      setObjectValidationToast({
        show: true,
        message: inner,
        duration,
        type: 'danger',
      });
    };

    crmSocket.on('object_validation_toast', onObjectValidationToast);
    return () => {
      crmSocket.off('object_validation_toast', onObjectValidationToast);
    };
  }, [crmSocket]);

  const dismissIncomingNotificationToast = useCallback(() => {
    setIncomingNotificationToast({ show: false, item: null });
  }, []);

  const dismissObjectValidationToast = useCallback(() => {
    setObjectValidationToast((prev) => ({ ...prev, show: false, message: null }));
  }, []);

  const fetchWhatsAppSettings = async () => {
    try {
      const dataApi = new DataApi('whatsapp_setting');
      const response = await dataApi.fetchAll();

      if (response.data.success) {
        console.log('WhatsApp Settings:', response);
        const selectedWhatsAppSetting = sessionStorage.getItem(
          'selectedWhatsAppSetting'
        );
        const initialSetting = response.data.record.find(
          record => record.phone === selectedWhatsAppSetting
        )
          ? selectedWhatsAppSetting
          : (() => {
            const newPhone = response.data.record[0]?.phone;
            sessionStorage.setItem('selectedWhatsAppSetting', newPhone);
            return newPhone;
          })();

        onWhatsAppSettingChange(initialSetting);
        setSelectedSetting(initialSetting);
        setSelectedWhatsAppSetting(initialSetting);
        setWhatsappSetting(response.data.record);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
      setWhatsappSetting([]);
    }
  };

  const fetchUnreadMessages = async (offset = 0, append = false) => {
    if (!hasPackage('watconnect')) {
      setUnreadMessages([]);
      setTotalUnreadCount(0);
      setWhatsappOffset(0);
      setHasMoreWhatsapp(false);
      return;
    }
    if (!selectedSetting) return;

    try {
      setIsLoadingWhatsapp(true);
      const LIMIT = 10;
      const currentOffset = offset;
      const dataApi = new DataApi('watconnect_history');
      let result = await dataApi.fetchAll(
        `filter=unread&search=&businessNumber=${encodeURIComponent(selectedSetting || '')}&limit=${LIMIT}&offset=${currentOffset}`
      );

      console.log('Unread messages API response:', result);

      if (result && result.data && result.data.success && result.data.records) {
        const newRecords = result.data.records;
        if (append) {
          setUnreadMessages(prev => [...prev, ...newRecords]);
        } else {
          setUnreadMessages(newRecords);
          setWhatsappOffset(0);
        }
        setHasMoreWhatsapp(newRecords.length >= LIMIT);

        if (result.data.totalCount !== undefined) {
          setTotalUnreadCount(result.data.totalCount);
        } else if (!append) {
          setTotalUnreadCount(newRecords.length);
        }
      } else {
        console.log('No unread messages found');
        if (!append) {
          setUnreadMessages([]);
          setTotalUnreadCount(0);
          setWhatsappOffset(0);
        }
        setHasMoreWhatsapp(false);
      }
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      if (!append) {
        setUnreadMessages([]);
        setTotalUnreadCount(0);
        setWhatsappOffset(0);
      }
      setHasMoreWhatsapp(false);
    } finally {
      setIsLoadingWhatsapp(false);
    }
  };

  const handleLoadMoreWhatsapp = () => {
    if (isLoadingWhatsapp || !hasMoreWhatsapp) return;
    const nextOffset = whatsappOffset + 10;
    setWhatsappOffset(nextOffset);
    fetchUnreadMessages(nextOffset, true);
  };

  // Mark messages as read
  const markMessagesAsRead = async whatsappNumber => {
    try {
      const dataApi = new DataApi('watconnect_history/mark_as_read');
      const result = await dataApi.post(
        `?whatsapp_setting_number=${encodeURIComponent(selectedSetting || '')}`,
        { whatsapp_number: whatsappNumber }
      );

      console.log('Mark as read response:', result);

      if (result && result.data && result.data.success) {
        console.log('Messages marked as read successfully');
        // Refresh unread counts after marking as read
        fetchUnreadMessages();
        return true;
      } else {
        console.log('Failed to mark messages as read');
        return false;
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return false;
    }
  };

  const fetchSystemNotifications = useCallback(
    async (options = {}) => {
      const { toastNewest = false, detectNew = false } = options;
      try {
        setIsLoadingNotifications(true);
        const notificationApi = new DataApi('notifications');
        const response = await notificationApi.fetchAll(
          'limit=15&offset=0&unreadOnly=false'
        );
        const list =
          response && response.data && Array.isArray(response.data)
            ? response.data
            : [];

        if (toastNewest || detectNew) {
          for (const item of list) {
            const key = String(item.id ?? item.notification_id).toLowerCase();
            if (!seenNotificationIdsRef.current.has(key)) {
              applySystemNotification({ notification: item });
              break;
            }
          }
        }

        markNotificationsSeen(list);
        setSystemNotifications(list);
      } catch (error) {
        console.error('Error fetching system notifications:', error);
        setSystemNotifications([]);
      } finally {
        setIsLoadingNotifications(false);
      }
    },
    [applySystemNotification, markNotificationsSeen]
  );

  // Fetch system unread count
  const fetchSystemUnreadCount = async () => {
    try {
      const notificationApi = new DataApi('notifications');
      const response = await notificationApi.get('/unread-count');

      if (response && response.data && response.data.count !== undefined) {
        setSystemUnreadCount(response.data.count);
        console.log('[AppHeader] System unread count:', response.data.count);
      } else {
        console.warn('[AppHeader] Unexpected response format for unread count:', response);
        setSystemUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching system unread count:', error);
      setSystemUnreadCount(0);
    }
  };

  // Scheduled jobs Run now + cron fallback when socket missed
  useEffect(() => {
    const onRefresh = (event) => {
      const showToast = event?.detail?.showToast === true;
      fetchSystemNotifications({ toastNewest: showToast, detectNew: showToast });
      fetchSystemUnreadCount();
    };
    window.addEventListener('ibs-refresh-system-notifications', onRefresh);
    return () =>
      window.removeEventListener('ibs-refresh-system-notifications', onRefresh);
  }, [fetchSystemNotifications]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === 'visible') {
        fetchSystemNotifications({ detectNew: true });
        fetchSystemUnreadCount();
      }
    };
    const intervalId = window.setInterval(poll, 20000);
    return () => window.clearInterval(intervalId);
  }, [fetchSystemNotifications]);

  useEffect(() => {
    if (watconnectPackageEnabled) {
      fetchUnreadMessages();
    } else {
      setUnreadMessages([]);
      setTotalUnreadCount(0);
    }
    fetchSystemNotifications();
    fetchSystemUnreadCount();

    const notificationInterval = setInterval(() => {
      fetchSystemNotifications();
      fetchSystemUnreadCount();
    }, 900000);

    return () => clearInterval(notificationInterval);
  }, [selectedSetting, watconnectPackageEnabled, fetchSystemNotifications]);

  // Mark system notification as read
  const markSystemNotificationAsRead = async notificationId => {
    try {
      const notificationApi = new DataApi('notifications');
      await notificationApi.put(`/${notificationId}/read`);

      // Update local state
      setSystemNotifications(prev =>
        prev.map(notif =>
          (notif.id === notificationId || notif.notification_id === notificationId)
            ? { ...notif, is_read: true }
            : notif
        )
      );

      // Refresh unread count
      fetchSystemUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark task as complete from notification
  const markTaskAsComplete = async notification => {
    try {
      // Check if this is a task notification
      if (notification.related_object !== 'task' || !notification.parentid) {
        console.warn('Notification is not for a task or missing parentid');
        return false;
      }

      // Check if task is already completed
      if (notification.notification_type === 'task_closed') {
        console.log('Task is already completed');
        return false;
      }

      const taskId = notification.parentid;
      const notificationId = notification.id || notification.notification_id;

      // Set loading state
      setCompletingTaskId(taskId);

      // Update task status to Completed using DataApi
      const dataApi = new DataApi(`object/task`);
      const response = await dataApi.update({ status: 'Completed' }, taskId);

      if (response && response.data) {
        console.log('Task marked as complete successfully');

        // Delete the notification since task is completed
        await deleteSystemNotification(notificationId);

        return true;
      } else {
        console.error('Failed to mark task as complete:', response);
        return false;
      }
    } catch (error) {
      console.error('Error marking task as complete:', error);
      return false;
    } finally {
      // Clear loading state
      setCompletingTaskId(null);
    }
  };

  // Delete system notification
  const deleteSystemNotification = async notificationId => {
    try {
      const notificationApi = new DataApi('notifications');
      const response = await notificationApi.delete(notificationId);

      if (response && response.data && response.data.success) {
        // Remove from local state
        setSystemNotifications(prev =>
          prev.filter(notif =>
            notif.id !== notificationId && notif.notification_id !== notificationId
          )
        );

        // Refresh unread count
        fetchSystemUnreadCount();
        console.log('[AppHeader] Notification deleted successfully');
      } else {
        console.error('[AppHeader] Failed to delete notification');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const openSystemNotificationDetail = async notification => {
    if (!notification) return;
    const notificationId = notification.id || notification.notification_id;
    setShowNotificationDropdown(false);
    setSelectedSystemNotification(notification);
    setSystemNotificationModalOpen(true);

    if (!notification.is_read && notificationId) {
      await markSystemNotificationAsRead(notificationId);
      setSelectedSystemNotification(prev =>
        prev ? { ...prev, is_read: true } : prev
      );
      setSystemNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId || notif.notification_id === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );
    }

    if (notificationId) {
      setLoadingNotificationDetail(true);
      try {
        const notificationApi = new DataApi('notifications');
        const response = await notificationApi.fetchById(notificationId);
        if (response?.data?.id) {
          setSelectedSystemNotification(response.data);
        }
      } catch (error) {
        console.warn('[AppHeader] Could not load notification detail:', error);
      } finally {
        setLoadingNotificationDetail(false);
      }
    }
  };

  const handleSystemNotificationClick = notification => {
    void openSystemNotificationDetail(notification);
  };

  const handleOpenNotificationRecord = notification => {
    if (notification?.related_object && notification?.parentid) {
      setSystemNotificationModalOpen(false);
      navigate(`/view/${notification.related_object}/${notification.parentid}`);
    }
  };

  const handleDeleteNotificationFromModal = async notificationId => {
    if (!notificationId || !window.confirm('Delete this notification?')) return;
    setDeletingNotification(true);
    try {
      await deleteSystemNotification(notificationId);
      setSystemNotificationModalOpen(false);
      setSelectedSystemNotification(null);
    } finally {
      setDeletingNotification(false);
    }
  };

  const markAllSystemNotificationsAsRead = async () => {
    try {
      const notificationApi = new DataApi('notifications');
      await notificationApi.put('/read-all');
      await fetchSystemNotifications();
      await fetchSystemUnreadCount();
      setShowNotificationDropdown(false);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteAllSystemNotifications = async () => {
    setDeletingAllNotifications(true);
    try {
      const notificationApi = new DataApi('notifications');
      const response = await notificationApi.delete('delete-all');
      if (response?.data?.success !== false) {
        seenNotificationIdsRef.current = new Set();
        setSystemNotifications([]);
        setSystemUnreadCount(0);
        setShowNotificationDropdown(false);
        setShowDeleteAllNotificationsConfirm(false);
        if (systemNotificationModalOpen) {
          setSystemNotificationModalOpen(false);
          setSelectedSystemNotification(null);
        }
      }
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    } finally {
      setDeletingAllNotifications(false);
    }
  };

  const handleIncomingNotificationToastClick = () => {
    const item = incomingNotificationToast.item;
    dismissIncomingNotificationToast();
    if (item) {
      void openSystemNotificationDetail(item);
    }
  };

  // Format time for display (relative e.g. "Just now", "5 min ago")
  const formatTimeAgo = timestamp => {
    if (!timestamp) return 'Just now';

    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMs = now - date;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInMinutes < 1) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays}d ago`;
      } else {
        return formatDate(timestamp);
      }
    } catch (error) {
      return 'Just now';
    }
  };

  // Get sender display name
  const getSenderName = message => {
    return message.name || 'Unknown Sender';
  };

  // Get message preview text
  const getMessagePreview = message => {
    const unreadCount = parseInt(message.unread_msg_count || '0');
    if (unreadCount > 1) {
      return `${unreadCount} unread messages`;
    }
    return '1 unread message';
  };

  // Handle notification click - open WhatsApp chat
  const handleNotificationClick = async message => {
    console.log('Opening chat for:', message);

    // Close the dropdown
    setShowNotificationDropdown(false);

    // Mark messages as read first
    const markedAsRead = await markMessagesAsRead(message.whatsapp_number);

    if (markedAsRead) {
      // Set the selected user for chat
      setSelectedUser({
        name: message.name,
        whatsapp_number: message.whatsapp_number,
        unread_count: message.unread_msg_count,
      });

      // Set the WhatsApp setting
      setSelectedWhatsAppSetting(selectedSetting);

      // Navigate to watconnect/chat page with parameters
      navigate('/watconnect/chats', {
        state: {
          selectedContact: {
            name: message.name,
            whatsapp_number: message.whatsapp_number,
          },
          selectedWhatsAppSetting: selectedSetting,
        },
      });
    } else {
      console.log('Failed to mark messages as read, still navigating...');
      // Navigate even if marking as read failed
      navigate('/watconnect/chats', {
        state: {
          selectedContact: {
            name: message.name,
            whatsapp_number: message.whatsapp_number,
          },
          selectedWhatsAppSetting: selectedSetting,
        },
      });
    }
  };

  const handleSettingChange = e => {
    const selectedId = e.target.value;
    setSelectedSetting(selectedId);
    setSelectedWhatsAppSetting(selectedId);
    sessionStorage.setItem('selectedWhatsAppSetting', selectedId);
    if (onWhatsAppSettingChange) {
      onWhatsAppSettingChange(selectedId);
    }

    // Refresh unread messages when setting changes
    fetchUnreadMessages();
  };

  const handleLogout = () => {
    clearTelecmiSoftphoneSession();
    telecmiSpPasswordRef.current = null;
    setTelecmiPwInMemory(false);
    clearPermissionCache();
    authApi.logout();
    navigate('/login');
  };

  const handleTelecmiSoftphoneLogin = async () => {
    const id = String(telecmiLoginId || '').trim();
    if (!id || !telecmiLoginPw) {
      setTelecmiSpMsg({
        type: 'danger',
        text: 'Enter TeleCMI user id and password (from the TeleCMI dashboard).',
      });
      return;
    }
    setTelecmiSpBusy(true);
    setTelecmiSpMsg({ type: '', text: '' });
    try {
      const res = await TelecmiApi.userLogin({ id, password: telecmiLoginPw });
      const payload = res?.data?.data;
      const tok = payload?.token;
      const agent = payload?.agent;
      if (!tok || !agent) {
        throw new Error(res?.data?.message || 'Unexpected TeleCMI response');
      }
      writeTelecmiSoftphoneSession(tok, agent);
      telecmiSpPasswordRef.current = telecmiLoginPw;
      setTelecmiPwInMemory(true);
      setTelecmiLoginPw('');
      setTelecmiSpSession({ token: tok, agent });
      setTelecmiSpMsg({
        type: 'success',
        text: payload?.msg || 'Signed in to TeleCMI. You can place a call.',
      });
    } catch (err) {
      const text =
        err?.response?.data?.message ||
        err?.message ||
        'TeleCMI login failed.';
      setTelecmiSpMsg({ type: 'danger', text });
      setTelecmiSpSession(null);
    } finally {
      setTelecmiSpBusy(false);
    }
  };

  const handleTelecmiSoftphoneLogout = async () => {
    const session = telecmiSpSession || readTelecmiSoftphoneSession();
    const agent = session?.agent;
    const pw = telecmiSpPasswordRef.current || telecmiLogoutPw;
    if (!agent?.id) {
      clearTelecmiSoftphoneSession();
      telecmiSpPasswordRef.current = null;
      setTelecmiPwInMemory(false);
      setTelecmiSpSession(null);
      setTelecmiSpMsg({
        type: 'warning',
        text: 'TeleCMI session cleared.',
      });
      return;
    }
    if (!pw) {
      setTelecmiSpMsg({
        type: 'warning',
        text: 'Enter your TeleCMI password to log out (e.g. after a page refresh).',
      });
      return;
    }
    setTelecmiSpBusy(true);
    setTelecmiSpMsg({ type: '', text: '' });
    try {
      await TelecmiApi.userLogout({
        agent_id: String(agent.id).trim(),
        password: pw,
        category: agent.category,
      });
      clearTelecmiSoftphoneSession();
      telecmiSpPasswordRef.current = null;
      setTelecmiPwInMemory(false);
      setTelecmiLogoutPw('');
      setTelecmiSpSession(null);
      setTelecmiSpMsg({
        type: 'success',
        text: 'Signed out of TeleCMI softphone.',
      });
    } catch (err) {
      const text =
        err?.response?.data?.message ||
        err?.message ||
        'TeleCMI logout failed.';
      setTelecmiSpMsg({ type: 'danger', text });
    } finally {
      setTelecmiSpBusy(false);
    }
  };

  const handleDialerCall = async () => {
    const normalized = String(dialerNumber || '').replace(/\D/g, '');
    if (!normalized) {
      setDialerMessage({ type: 'danger', text: 'Enter a valid phone number.' });
      return;
    }
    setDialerLoading(true);
    setDialerMessage({ type: '', text: '' });
    const toNum = Number(normalized);
    const built = buildTelecmiInitiatePayload(toNum, { crm: true });
    if (built.error) {
      setDialerLoading(false);
      setDialerMessage({ type: 'danger', text: built.error });
      return;
    }
    emitTelecmiOutboundWaiting({ to: toNum });
    try {
      const res = await TelecmiApi.initiateCall(built.payload);
      const message =
        res?.data?.data?.msg ||
        res?.data?.msg ||
        res?.data?.message ||
        res?.data?.data?.message ||
        'Call initiated successfully.';
      setDialerMessage({ type: 'success', text: message });
    } catch (error) {
      const message =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate call.';
      emitTelecmiOutboundFailed({ message, to: toNum });
      setDialerMessage({ type: 'danger', text: message });
    } finally {
      setDialerLoading(false);
    }
  };

  const handleDialerKeyPress = (key) => {
    setDialerMessage({ type: '', text: '' });
    setDialerNumber((prev) => `${prev}${key}`);
  };

  const handleDialerBackspace = () => {
    setDialerMessage({ type: '', text: '' });
    setDialerNumber((prev) => prev.slice(0, -1));
  };

  const handleDialerClear = () => {
    setDialerMessage({ type: '', text: '' });
    setDialerNumber('');
  };

  return (
    <>
      {/* Top Utility Bar */}
      <div
        style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e5e5e5',
          position: 'sticky',
          top: 0,
          zIndex: 1031,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div className='container-fluid'>
          <div className='d-flex align-items-center justify-content-between py-2'>
            {/* Left: company logo (public.company.logourl) + name from JWT */}
            <div className='d-flex align-items-center gap-3 min-w-0'>
              {headerLogoSrc ? (
                <img
                  src={headerLogoSrc}
                  alt=''
                  className='flex-shrink-0'
                  style={{
                    height: 50,
                    maxHeight: 50,
                    maxWidth: 220,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              ) : null}
              <div className='d-flex flex-column min-w-0'>
                <span
                  className='text-truncate'
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#0176d3',
                    letterSpacing: '0.3px',
                    lineHeight: 1.2,
                  }}
                  title={userInfo.companyname || 'zemneo'}
                >
                  {userInfo.companyname || 'zemneo'}
                </span>
                {userInfo.companyname ? (
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#706e6b',
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    zemneo
                  </span>
                ) : null}
              </div>
            </div>

            {/* Center: Search Bar */}
            <div className="gs-header-search">
              <button
                type="button"
                className="gs-header-search-btn"
                onClick={() => setShowGlobalSearch(true)}
                aria-label="Open global search"
              >
                <FaSearch aria-hidden="true" />
                <span>Search records, apps and actions</span>
                <span className="gs-header-search-kbd">⌘K</span>
              </button>
            </div>


            {/* Right: Icons */}
            <div className='d-flex align-items-center gap-3'>
              {hasCtiSolutionEnabled && (
                <Button
                  variant='link'
                  className='p-2'
                  style={{ color: '#706e6b' }}
                  onClick={() => {
                    if (userInfo?.enable_telecmi) {
                      setTelecmiSpSession(readTelecmiSoftphoneSession());
                    }
                    setShowDialer(true);
                    setDialerMessage({ type: '', text: '' });
                  }}
                  title='Dialer'
                >
                  <FaPhone size={18} />
                </Button>
              )}

              {/* WhatsApp Dropdown */}
              {watconnectPackageEnabled &&
                whatsappSetting.length > 0 &&
                userInfo.userrole !== 'SUPER_ADMIN' && (
                  <Form.Select
                    value={selectedSetting}
                    onChange={handleSettingChange}
                    size='sm'
                    style={{
                      maxWidth: '250px',
                      fontSize: '13px',
                      border: '1px solid #dddbda',
                    }}
                  >
                    {whatsappSetting.map(setting => (
                      <option key={setting.phone} value={setting.phone}>
                        {setting.name} ({setting.phone})
                      </option>
                    ))}
                  </Form.Select>
                )}

              {/* Settings Icon - from license entitlements (setup.showSetup + allowed items) */}
              {showSetupChrome && (
                <Button
                  variant='link'
                  className='p-2'
                  style={{ color: '#706e6b' }}
                  onClick={openSetupInNewTab}
                  title='Setup (opens in new tab)'
                >
                  <FaSettings size={18} />
                </Button>
              )}

              {/* Bell Icon - Notifications Dropdown */}
              <Dropdown show={showNotificationDropdown} onToggle={setShowNotificationDropdown}>
                <Dropdown.Toggle
                  as={Button}
                  variant='link'
                  className='p-2'
                  style={{
                    color: '#706e6b',
                    position: 'relative',
                    textDecoration: 'none',
                    border: 'none',
                  }}
                >
                  <FaBell size={18} />
                  {((watconnectPackageEnabled ? totalUnreadCount : 0) + systemUnreadCount) >
                    0 && (
                      <Badge
                        bg='danger'
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          fontSize: '10px',
                          padding: '2px 5px',
                          borderRadius: '50%',
                          minWidth: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {((watconnectPackageEnabled ? totalUnreadCount : 0) + systemUnreadCount) >
                          99
                          ? '99+'
                          : (watconnectPackageEnabled ? totalUnreadCount : 0) +
                          systemUnreadCount}
                      </Badge>
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  align='end'
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '380px',
                    maxWidth: '400px',
                    padding: '0',
                    marginTop: '8px',
                    maxHeight: '500px',
                  }}
                >
                  {/* Tabs Header */}
                  <div
                    style={{
                      padding: '0',
                      borderBottom: '1px solid #eee',
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                    }}
                  >
                    <button
                      onClick={() => setActiveNotificationTab('system')}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: 'none',
                        background: activeNotificationTab === 'system' ? 'white' : 'transparent',
                        borderBottom: activeNotificationTab === 'system' ? '2px solid #0176d3' : '2px solid transparent',
                        cursor: 'pointer',
                        fontWeight: activeNotificationTab === 'system' ? '600' : '500',
                        fontSize: '14px',
                        color: activeNotificationTab === 'system' ? '#0176d3' : '#666',
                        transition: 'all 0.2s',
                      }}
                    >
                      Notifications
                      {systemUnreadCount > 0 && (
                        <Badge
                          bg='danger'
                          style={{
                            marginLeft: '6px',
                            fontSize: '10px',
                          }}
                        >
                          {systemUnreadCount}
                        </Badge>
                      )}
                    </button>
                    {watconnectPackageEnabled && (
                      <button
                        onClick={() => setActiveNotificationTab('whatsapp')}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          border: 'none',
                          background: activeNotificationTab === 'whatsapp' ? 'white' : 'transparent',
                          borderBottom: activeNotificationTab === 'whatsapp' ? '2px solid #25D366' : '2px solid transparent',
                          cursor: 'pointer',
                          fontWeight: activeNotificationTab === 'whatsapp' ? '600' : '500',
                          fontSize: '14px',
                          color: activeNotificationTab === 'whatsapp' ? '#25D366' : '#666',
                          transition: 'all 0.2s',
                        }}
                      >
                        WatConnect
                        {totalUnreadCount > 0 && (
                          <Badge
                            bg='danger'
                            style={{
                              marginLeft: '6px',
                              fontSize: '10px',
                            }}
                          >
                            {totalUnreadCount}
                          </Badge>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {activeNotificationTab === 'system' ? (
                      // System Notifications Tab
                      isLoadingNotifications ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#999' }}>
                          Loading notifications...
                        </div>
                      ) : (
                        <NotificationList
                          notifications={systemNotifications}
                          onNotificationClick={handleSystemNotificationClick}
                          onMarkAsRead={markSystemNotificationAsRead}
                          onDelete={deleteSystemNotification}
                          onMarkTaskComplete={markTaskAsComplete}
                          completingTaskId={completingTaskId}
                          formatTime={formatTime}
                          formatDate={formatDate}
                          formatDateTime={formatDateTime}
                        />
                      )
                    ) : (
                      // WhatsApp Messages Tab
                      <>
                        {unreadMessages.length > 0 ? (
                          unreadMessages.map((message, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '16px 20px',
                                borderBottom: '1px solid #f0f0f0',
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                              }}
                              onMouseEnter={e =>
                                (e.currentTarget.style.backgroundColor = '#f8f9fa')
                              }
                              onMouseLeave={e =>
                                (e.currentTarget.style.backgroundColor = 'white')
                              }
                              onClick={() => handleNotificationClick(message)}
                            >
                              <div className='d-flex align-items-start gap-3'>
                                {/* WhatsApp Icon */}
                                <div
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    backgroundColor: '#25D366',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    flexShrink: 0,
                                    marginTop: '2px',
                                  }}
                                >
                                  <FaWhatsapp size={18} />
                                </div>

                                {/* Message Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className='d-flex justify-content-between align-items-start mb-1'>
                                    <div
                                      style={{
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#333',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {getSenderName(message)}
                                    </div>
                                    {parseInt(message.unread_msg_count || '0') >
                                      0 && (
                                        <Badge
                                          bg='danger'
                                          style={{
                                            fontSize: '10px',
                                            marginLeft: '8px',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {message.unread_msg_count}
                                        </Badge>
                                      )}
                                  </div>

                                  <div
                                    style={{
                                      fontSize: '13px',
                                      color: '#666',
                                      marginBottom: '4px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {getMessagePreview(message)}
                                  </div>

                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#999',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span>WhatsApp: {message.whatsapp_number}</span>
                                    <span>{formatTimeAgo(message.createddate)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              padding: '40px 20px',
                              textAlign: 'center',
                              color: '#999',
                            }}
                          >
                            <FaWhatsapp
                              size={32}
                              style={{
                                marginBottom: '12px',
                                opacity: 0.3,
                                color: '#25D366',
                              }}
                            />
                            <div style={{ fontSize: '14px' }}>
                              No unread WatConnect messages
                            </div>
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                              All messages are read
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* View All Footer */}
                  {activeNotificationTab === 'whatsapp' && (unreadMessages.length > 0 || hasMoreWhatsapp) && (
                    <div
                      style={{
                        padding: '12px 20px',
                        borderTop: '1px solid #eee',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                      }}
                    >
                      {hasMoreWhatsapp && (
                        <Button
                          variant='link'
                          disabled={isLoadingWhatsapp}
                          style={{
                            color: '#0176d3',
                            textDecoration: 'none',
                            fontSize: '13px',
                            fontWeight: '500',
                            padding: 0,
                          }}
                          onClick={handleLoadMoreWhatsapp}
                        >
                          {isLoadingWhatsapp ? 'Loading...' : 'Load More'}
                        </Button>
                      )}
                      {hasMoreWhatsapp && unreadMessages.length > 0 && (
                        <span style={{ color: '#ccc' }}>|</span>
                      )}
                      {unreadMessages.length > 0 && (
                        <Button
                          variant='link'
                          style={{
                            color: '#0176d3',
                            textDecoration: 'none',
                            fontSize: '13px',
                            fontWeight: '500',
                            padding: 0,
                          }}
                          onClick={() => {
                            setShowNotificationDropdown(false);
                            navigate('/watconnect/chats');
                          }}
                        >
                          View All Chats
                        </Button>
                      )}
                    </div>
                  )}
                  {activeNotificationTab === 'system' && (
                    <>
                      <div
                        style={{
                          padding: '12px 20px',
                          borderTop: '1px solid #eee',
                          textAlign: 'center',
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        <Button
                          variant='link'
                          style={{
                            color: '#0176d3',
                            textDecoration: 'none',
                            fontSize: '13px',
                            fontWeight: '500',
                            padding: 0,
                          }}
                          onClick={() => {
                            setShowNotificationDropdown(false);
                            navigate('/notifications');
                          }}
                        >
                          View All Notifications
                        </Button>
                      </div>
                      {systemNotifications.length > 0 && (
                        <div
                          style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #eee',
                            backgroundColor: '#f8f9fa',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '16px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            variant='link'
                            style={{
                              color: '#0176d3',
                              textDecoration: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              padding: 0,
                            }}
                            onClick={() => void markAllSystemNotificationsAsRead()}
                          >
                            Mark all as read
                          </Button>
                          <Button
                            variant='link'
                            style={{
                              color: '#dc3545',
                              textDecoration: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              padding: 0,
                            }}
                            onClick={() => setShowDeleteAllNotificationsConfirm(true)}
                          >
                            Delete all
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>

              {/* User Icon - User Profile Dropdown */}
              <Dropdown>
                <Dropdown.Toggle
                  as={Button}
                  variant='link'
                  className='p-2'
                  style={{
                    color: '#706e6b',
                    position: 'relative',
                    textDecoration: 'none',
                    border: 'none',
                  }}
                >
                  <FaUser size={18} />
                </Dropdown.Toggle>

                <Dropdown.Menu
                  align='end'
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '320px',
                    padding: '0',
                    marginTop: '8px',
                  }}
                >
                  {/* User Profile Header — name, username, role */}
                  <div
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #eee',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <div className='d-flex align-items-center gap-3'>
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#0176d3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '18px',
                          fontWeight: 'bold',
                        }}
                      >
                        {profileMenuLabels.initials}
                      </div>

                      <div className='flex-grow-1'>
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#333',
                          }}
                        >
                          {profileMenuLabels.displayName}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {profileMenuLabels.username}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {profileMenuLabels.role}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ padding: '8px 0' }}>
                    <Dropdown.Item
                      as={NavLink}
                      to='/profile'
                      style={{
                        color: '#032d60',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <FaUser size={14} className='me-2' />
                      View Profile
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={() => setShowManageTabsModal(true)}
                      style={{
                        color: '#032d60',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <FaThList size={14} className='me-2' />
                      Manage Tabs
                    </Dropdown.Item>

                    <Dropdown.Item
                      as={NavLink}
                      to='/recycle-bin'
                      style={{
                        color: '#032d60',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <BsTrash size={14} className='me-2' />
                      Recycle Bin
                    </Dropdown.Item>

                    {showSetupChrome && (
                      <Dropdown.Item
                        onClick={openSetupInNewTab}
                        style={{
                          color: '#0176d3',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        <FaSettings size={14} className='me-2' />
                        Setup
                      </Dropdown.Item>
                    )}

                    <Dropdown.Item
                      onClick={handleLogout}
                      style={{
                        color: '#dc3545',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderTop: '1px solid #eee',
                      }}
                    >
                      <FaSignOutAlt size={14} className='me-2' />
                      Log Out
                    </Dropdown.Item>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      {/* Main Menu Navigation Bar */}
      <Navbar
        bg='light'
        variant='light'
        expand='lg'
        style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e5e5',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          width: '100%',
          overflow: 'visible',
        }}
      >
        <div
          className='container-fluid menu-nav-container'
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingLeft: '20px',
            overflow: 'visible',
            width: '100%',
            minWidth: 0,
          }}
        >
          <Nav
            className='menu-primary-nav'
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              flex: '1 1 auto',
              minWidth: 0,
              width: '100%',
              overflow: 'visible',
            }}
          >
            {/* App Launcher: only when App Manager has at least one app */}
            {showAppSwitcher && (
              <div className="menu-app-launcher-wrap">
                <button
                  ref={appLauncherToggleRef}
                  type="button"
                  className="btn btn-link btn-sm text-dark text-decoration-none d-flex align-items-center border rounded"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    borderColor: '#dee2e6',
                  }}
                  onClick={(e) => {
                    const next = !showAppLauncher;
                    setShowAppLauncher(next);
                    if (next) updateLauncherPosition(e.currentTarget);
                  }}
                  aria-expanded={showAppLauncher}
                  aria-haspopup="true"
                >
                  <FaTh size={14} className="me-1" />
                  {selectedAppId
                    ? (apps.find((a) => a.id === selectedAppId)?.name || 'App')
                    : 'Default App'}
                  <FaChevronDown size={10} className="ms-1" />
                </button>
                {showAppLauncher &&
                  launcherPosition &&
                  (() => {
                    const q = (appLauncherSearch || '').trim().toLowerCase();
                    const filteredApps = eligibleAppsForLauncher.filter(
                      (app) => !q || (app.name || '').toLowerCase().includes(q) || (app.description || '').toLowerCase().includes(q)
                    );
                    const recentIds = getRecentAppIds();
                    const recentApps = recentIds
                      .map((id) => apps.find((a) => a.id === id))
                      .filter(Boolean)
                      .filter((app) => filteredApps.some((a) => a.id === app.id));
                    const allItems = [{ id: '', name: 'Default App', description: 'All tabs in default order', config: { icon: 'preset:default' } }, ...filteredApps];
                    return createPortal(
                      <div
                        ref={appLauncherPanelRef}
                        className="app-launcher-panel p-0 bg-white border shadow-sm"
                        role="menu"
                        style={{
                          position: 'fixed',
                          top: launcherPosition.top,
                          left: launcherPosition.left,
                          right: 'auto',
                          minWidth: 380,
                          maxWidth: 420,
                          maxHeight: 'min(70vh, 440px)',
                          zIndex: 1060,
                          borderRadius: 12,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        }}
                      >
                        <div className="app-launcher-search-wrap position-relative">
                          <FaSearch className="app-launcher-search-icon" size={14} />
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search apps and items..."
                            value={appLauncherSearch}
                            onChange={(e) => setAppLauncherSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Search apps"
                          />
                        </div>
                        <div className="app-launcher-body">
                          {recentApps.length > 0 && !q && (
                            <>
                              <div className="app-launcher-section-title">Frequently used</div>
                              <div className="app-launcher-grid mb-3">
                                {recentApps.map((app) => {
                                  const isSelected = selectedAppId === app.id;
                                  return (
                                    <button
                                      key={app.id}
                                      type="button"
                                      role="menuitem"
                                      className={`app-launcher-card ${isSelected ? 'is-active' : ''}`}
                                      onClick={() => { handleAppSelect(app.id); setShowAppLauncher(false); setAppLauncherSearch(''); }}
                                    >
                                      <div className="app-launcher-card-icon">
                                        <AppIconDisplay icon={app?.config?.icon} size={32} />
                                      </div>
                                      <span className="app-launcher-card-title">{app.name}</span>
                                      {app.description && <span className="app-launcher-card-desc">{app.description}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                          <div className="app-launcher-section-title">{recentApps.length > 0 && !q ? 'All apps' : 'Apps'}</div>
                          <div className="app-launcher-grid">
                            {allItems.map((item) => {
                              const isDefault = item.id === '';
                              const isSelected = isDefault ? !selectedAppId : selectedAppId === item.id;
                              return (
                                <button
                                  key={isDefault ? '__default__' : item.id}
                                  type="button"
                                  role="menuitem"
                                  className={`app-launcher-card ${isSelected ? 'is-active' : ''}`}
                                  onClick={() => {
                                    if (isDefault) handleAppSelect('');
                                    else handleAppSelect(item.id);
                                    setShowAppLauncher(false);
                                    setAppLauncherSearch('');
                                  }}
                                >
                                  <div className="app-launcher-card-icon">
                                    <AppIconDisplay icon={item.config?.icon} size={32} />
                                  </div>
                                  <span className="app-launcher-card-title">{item.name}</span>
                                  {item.description && <span className="app-launcher-card-desc">{item.description}</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>,
                      document.body
                    );
                  })()}
              </div>
            )}
            <Menu selectedAppId={selectedAppId || undefined} />
          </Nav>
        </div>
      </Navbar>

      {showDialer && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowDialer(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '420px',
              background: '#fff',
              borderRadius: '10px',
              padding: '18px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <h6 className='mb-0'>CTI Dialer</h6>
              <Button variant='link' className='p-0 text-muted' onClick={() => setShowDialer(false)}>
                Close
              </Button>
            </div>

            {userInfo?.enable_telecmi && (
              <div className='border rounded p-3 mb-3 bg-light'>
                <div className='small text-muted mb-2 fw-semibold'>TeleCMI (sign in to place calls)</div>
                {telecmiSpSession?.token && telecmiSpSession?.agent ? (
                  <div className='d-flex flex-column gap-2'>
                    <div className='small'>
                      Signed in:{' '}
                      <strong>{telecmiSpSession.agent.name || telecmiSpSession.agent.id}</strong>
                      <span className='text-muted'> ({telecmiSpSession.agent.id})</span>
                    </div>
                    {!telecmiPwInMemory && (
                      <Form.Control
                        type='password'
                        size='sm'
                        value={telecmiLogoutPw}
                        onChange={(e) => setTelecmiLogoutPw(e.target.value)}
                        placeholder='TeleCMI password (to log out after refresh)'
                        autoComplete='off'
                      />
                    )}
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        disabled={telecmiSpBusy}
                        onClick={handleTelecmiSoftphoneLogout}
                      >
                        Log out
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    <Form.Control
                      size='sm'
                      value={telecmiLoginId}
                      onChange={(e) => setTelecmiLoginId(e.target.value)}
                      placeholder='TeleCMI user id (e.g. 1001_33337620)'
                      autoComplete='username'
                    />
                    <Form.Control
                      type='password'
                      size='sm'
                      value={telecmiLoginPw}
                      onChange={(e) => setTelecmiLoginPw(e.target.value)}
                      placeholder='TeleCMI password'
                      autoComplete='current-password'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !telecmiSpBusy) handleTelecmiSoftphoneLogin();
                      }}
                    />
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='primary'
                        size='sm'
                        disabled={telecmiSpBusy}
                        onClick={handleTelecmiSoftphoneLogin}
                      >
                        {telecmiSpBusy ? 'Signing in…' : 'Sign in to TeleCMI'}
                      </Button>
                    </div>
                  </div>
                )}
                {telecmiSpMsg.text && (
                  <div
                    className={`alert alert-${telecmiSpMsg.type === 'success' ? 'success' : telecmiSpMsg.type === 'warning' ? 'warning' : 'danger'} py-1 px-2 mt-2 mb-0 small`}
                  >
                    {telecmiSpMsg.text}
                  </div>
                )}
              </div>
            )}

            <InputGroup className='mb-2'>
              <InputGroup.Text>
                <FaPhone />
              </InputGroup.Text>
              <Form.Control
                value={dialerNumber}
                onChange={(e) => {
                  setDialerMessage({ type: '', text: '' });
                  setDialerNumber(e.target.value);
                }}
                placeholder='Enter number'
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !dialerLoading) {
                    handleDialerCall();
                  }
                }}
              />
            </InputGroup>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              {dialerKeys.map((key) => (
                <Button
                  key={key}
                  variant='outline-secondary'
                  onClick={() => handleDialerKeyPress(key)}
                  disabled={dialerLoading}
                  style={{ fontWeight: 600 }}
                >
                  {key}
                </Button>
              ))}
            </div>

            {dialerMessage.text && (
              <div
                className={`alert alert-${dialerMessage.type === 'danger' ? 'danger' : 'success'} py-2 mb-2`}
                style={{ fontSize: '13px' }}
              >
                {dialerMessage.text}
              </div>
            )}

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='outline-dark'
                onClick={handleDialerBackspace}
                disabled={dialerLoading || !dialerNumber}
              >
                Backspace
              </Button>
              <Button
                variant='outline-warning'
                onClick={handleDialerClear}
                disabled={dialerLoading || !dialerNumber}
              >
                Clear
              </Button>
              <Button
                variant='outline-secondary'
                onClick={() => setShowDialer(false)}
                disabled={dialerLoading}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleDialerCall}
                disabled={
                  dialerLoading ||
                  (userInfo?.enable_telecmi &&
                    userInfo?.require_telecmi_login &&
                    !telecmiSpSession?.token)
                }
                title={
                  userInfo?.enable_telecmi &&
                    userInfo?.require_telecmi_login &&
                    !telecmiSpSession?.token
                    ? 'Sign in to TeleCMI above first'
                    : 'Place call'
                }
              >
                {dialerLoading ? 'Calling...' : 'Call'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Notification
        key={
          incomingNotificationToast.item?.id ??
          incomingNotificationToast.item?.notification_id ??
          'system-notification'
        }
        show={incomingNotificationToast.show}
        type='success'
        duration={SYSTEM_NOTIFICATION_DURATION_MS}
        onClose={dismissIncomingNotificationToast}
        containerClassName='app-header-system-notification'
        message={
          incomingNotificationToast.item ? (
            <div className='system-notification-content'>
              <div className='d-flex align-items-start gap-2'>
                <FaBell
                  className='mt-1 flex-shrink-0'
                  style={{ color: '#28a745', fontSize: '18px' }}
                  aria-hidden
                />
                <div className='flex-grow-1 min-w-0'>
                  <div className='system-notification-title'>
                    {incomingNotificationToast.item.title || 'Notification'}
                  </div>
                  <div className='system-notification-meta'>
                    {incomingNotificationToast.item.createddate
                      ? formatTime(incomingNotificationToast.item.createddate)
                      : 'Just now'}
                  </div>
                  {incomingNotificationToast.item.message ? (
                    <div className='system-notification-body'>
                      {incomingNotificationToast.item.message}
                    </div>
                  ) : null}
                  {incomingNotificationToast.item.task_description ? (
                    <div className='system-notification-task'>
                      {incomingNotificationToast.item.task_description.length > 200
                        ? `${incomingNotificationToast.item.task_description.slice(0, 200)}…`
                        : incomingNotificationToast.item.task_description}
                    </div>
                  ) : null}
                  {incomingNotificationToast.item.related_object &&
                    incomingNotificationToast.item.parentid ? (
                    <button
                      type='button'
                      className='btn btn-link p-0 mt-2 system-notification-open'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleIncomingNotificationToastClick();
                      }}
                    >
                      Open record
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <Notification
        key='object-validation-toast'
        show={objectValidationToast.show}
        type={objectValidationToast.type || 'danger'}
        duration={objectValidationToast.duration ?? 8000}
        onClose={dismissObjectValidationToast}
        containerClassName='app-header-object-validation-toast'
        message={objectValidationToast.message}
      />

      <SystemNotificationDetailModal
        show={systemNotificationModalOpen}
        onHide={() => {
          setSystemNotificationModalOpen(false);
          setSelectedSystemNotification(null);
        }}
        notification={selectedSystemNotification}
        loading={loadingNotificationDetail}
        onOpenRecord={handleOpenNotificationRecord}
        onDelete={handleDeleteNotificationFromModal}
        deleting={deletingNotification}
      />

      <DeleteConfirmationModal
        show={showDeleteAllNotificationsConfirm}
        onHide={() => {
          if (!deletingAllNotifications) {
            setShowDeleteAllNotificationsConfirm(false);
          }
        }}
        onConfirm={deleteAllSystemNotifications}
        title='Delete all notifications'
        message='Remove every notification from your list? This cannot be undone.'
        confirmText='Delete all'
        isDeleting={deletingAllNotifications}
      />

      <ManageTabsModal
        show={showManageTabsModal}
        onClose={() => setShowManageTabsModal(false)}
      />
      <GlobalSearchPalette
        open={showGlobalSearch}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onClose={() => setShowGlobalSearch(false)}
        apps={eligibleAppsForLauncher}
        setupItems={setupPaletteItems}
        inputRef={globalSearchInputRef}
      />
    </>
  );
};

export default AppHeader;
